import React, { useState, useEffect } from "react";
import { ResponsiveImage } from "../../../common/ResponsiveImage";
import { PollContent } from "../../../../types/newtypes";
import { useUpdateUserTagMutation } from "../../../../store/api/postgresUserApi";
import { useDispatch } from "react-redux";
import { postgresApi } from "../../../../store/api/postgresApi";

interface PollProps {
  content: PollContent;
  // eslint-disable-next-line no-unused-vars
  onComplete?: (tagKey: string, tagValue: string) => void;
  lessonId?: string;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const Poll: React.FC<PollProps> = ({
  content,
  onComplete,
  lessonId,
  onCardStateChange,
}) => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [updateUserTag] = useUpdateUserTagMutation();
  const dispatch = useDispatch();

  // Set initial state - poll requires user interaction before proceeding
  useEffect(() => {
    onCardStateChange?.(false, false);
  }, [onCardStateChange]);

  const handleVote = async (index: number, tagValue: string) => {
    if (!hasVoted) {
      setSelectedOption(index);
      setHasVoted(true);

      // Store the user's response as a tag
      if (content.tag_key) {
        try {
          await updateUserTag({
            tag_key: content.tag_key,
            tag_value: tagValue,
          }).unwrap();
          // Invalidate lesson so conditional cards refresh with updated tags
          if (lessonId) {
            dispatch(
              postgresApi.util.invalidateTags([
                { type: "Lessons", id: lessonId },
              ]),
            );
          }
        } catch (e) {
          // Non-blocking: still proceed locally
          // console.error('Failed to update user tag', e);
        }
      }
      onComplete?.(content.tag_key || "", tagValue);
      // Allow navigation to proceed
      onCardStateChange?.(true, true);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      {/* Slide Title */}
      {content.slide_title && (
        <h2 className="text-3xl font-bold mb-4 text-gray-800">
          {content.slide_title}
        </h2>
      )}

      {/* Intro Text */}
      {content.intro_text && (
        <p className="text-lg text-gray-600 mb-6">{content.intro_text}</p>
      )}

      {/* Image - smaller and centered */}
      {content.image_uri && (
        <div className="mb-6 flex justify-center">
          <div className="w-48 h-48">
            <ResponsiveImage
              image_uri={content.image_uri}
              alt={content.alt_text || "Poll illustration"}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Question - make it prominent */}
      {content.question && (
        <h3 className="text-xl font-semibold mb-6 text-center text-gray-800">
          {content.question}
        </h3>
      )}

      {/* Options */}
      <div className="space-y-3">
        {content.options?.map(
          (option: { text: string; tag_value: string }, index: number) => (
            <button
              key={index}
              onClick={() => handleVote(index, option.tag_value)}
              disabled={hasVoted}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                hasVoted
                  ? selectedOption === index
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 bg-gray-50 opacity-50"
                  : "border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`${hasVoted && selectedOption === index ? "font-semibold" : ""} text-gray-800`}
                >
                  {option.text}
                </span>
                {hasVoted && selectedOption === index && (
                  <span className="text-green-600">✓</span>
                )}
              </div>
            </button>
          ),
        )}
      </div>

      {/* Feedback message - no percentages for reflective polls */}
      {hasVoted && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-center text-gray-700">
            Thank you for sharing! Your response helps us personalise your
            learning journey.
          </p>
        </div>
      )}
    </div>
  );
};

export default Poll;
